import {
  center,
  innerCircleRadius,
  outerCircleRadius,
} from './radialGeometry.js';

// The hot-water dial's face — a web port of app/src/components/HotWaterRadial.
//
// A cylinder has no user setpoint, so where a heating zone gets the draggable
// Radial this shows the same circle carrying the ONE thing the app shows there:
// the live On/Off the cylinder is in right now (darwin_core's getDhwActiveMode),
// or "Offline" when the thermostat or the interface is down.
//
// It is PURELY PRESENTATIONAL and, matching the app (whose Pressable is rendered
// `disabled` with no onPress), NON-INTERACTIVE: not focusable, no pointer
// handlers, nothing to drag. The mode is changed from the Hot Water Status
// screen, reached from the action list beside it. So it is announced as one
// labelled image rather than a control, and the SVG is hidden from assistive tech.
//
// On and online tints the whole face brand green — the outer ring, the tap glyph
// and the big word — exactly as the app does; every other state is muted grey.
// The face itself (r=120 surface disc over an r=103 sunken centre) is the same
// geometry as the setpoint Radial, so the two dials read as one family.

// The ring the app draws outside the dial face: 4 units wide, so a circle stroked
// on r=132 spans 130–134 like the app's two stacked circles.
const ringRadius = 132;
const ringWidth = 4;

// The tap/cylinder outline, at the dial's scale. Same glyph as the action-list
// WaterIcon (components/icons.jsx) — copied from the app's own radial rather than
// nested from that 24px icon, so the placement inside the 380 viewBox stays the
// app's. Per the icons.jsx convention this is a deliberate manual copy: re-sync it
// if the app's geometry changes.
const glyphPath =
  'M16.2 14.483L14.518.877A1 1 0 0013.525 0H5.283a.73.73 0 00-.73.73c0 .413.315.76.726.8l3.916.388h0v2.687l-8.168.365a1.027 1.027 0 00-.737.362.74.74 0 00-.109.788c.63 1.372 1.682 2.058 3.157 2.058 2.44 0 4.897-.122 5.632 0 .49.082.812.447.963 1.096h0l.334 5.209H16.2z';
const glyphSpoutPath = 'M9.08974719 3.65513715L14.6700046 3.43913402';

export default function HotWaterRadial({ dhwMode = 'Off', offline = false }) {
  // Offline wins: a cylinder that last reported On is not heating water now, so
  // it must not keep the green face.
  const on = dhwMode === 'On' && !offline;
  // The face shouts the mode in caps; "Offline" keeps its sentence case, matching
  // the app and the setpoint dial's own offline readout.
  const status = offline ? 'Offline' : dhwMode;
  const word = offline ? status : status.toUpperCase();

  return (
    <div
      className={`hot-water-radial${on ? ' hot-water-radial--on' : ''}`}
      role="img"
      // Sentence case for the announcement (the face shouts it in caps).
      aria-label={status ? `Hot water ${status}` : 'Hot water'}
    >
      <svg
        className="hot-water-radial__svg"
        viewBox="0 0 380 380"
        aria-hidden="true"
      >
        <circle
          className="hot-water-radial__ring"
          cx={center.x}
          cy={center.y}
          r={ringRadius}
          strokeWidth={ringWidth}
          fill="none"
        />
        <circle
          className="hot-water-radial__bg-outer"
          cx={center.x}
          cy={center.y}
          r={outerCircleRadius}
        />
        <circle
          className="hot-water-radial__bg-inner"
          cx={center.x}
          cy={center.y}
          r={innerCircleRadius}
        />

        <g
          className="hot-water-radial__glyph"
          transform="translate(170 150) scale(2)"
          fill="none"
          strokeWidth={0.5}
          fillRule="evenodd"
        >
          <path d={glyphPath} strokeLinejoin="round" />
          <path d={glyphSpoutPath} />
        </g>

        {/* No mode to show (a zone that reported none) leaves the face blank
            rather than printing an empty word — the ring and glyph still read. */}
        {status && (
          <text
            className={`hot-water-radial__value${
              offline ? ' hot-water-radial__value--offline' : ''
            }`}
            x={center.x}
            y={center.y + 45}
            textAnchor="middle"
          >
            {word}
          </text>
        )}
      </svg>
    </div>
  );
}
